/**
 * CollectorStatus — the always-visible "is the data live?" indicator in the
 * footer chrome, off `GET /api/status`. A coloured dot + a state word + the age
 * of the last successful poll, linking to the full `/status` diagnostics page.
 *
 * Split into a pure `CollectorStatusView` (prop-driven, every state testable)
 * and the connected `CollectorStatus` that polls the endpoint. Colour is never
 * the only channel — the state word and the link's aria-label carry the meaning
 * (WCAG 2.1 AA).
 */

import { Link } from "react-router-dom";
import { useGetCollectorStatus } from "@/api/generated/status/status";
import { collectorSummary, type CollectorStatusSummary } from "@/lib/status";

/** How often to re-poll collector status (the collector itself polls every ~5 min). */
const REFETCH_INTERVAL_MS = 60_000;

interface CollectorStatusViewProps {
  summary: CollectorStatusSummary | undefined;
  isPending: boolean;
  isError: boolean;
}

export function CollectorStatusView({ summary, isPending, isError }: CollectorStatusViewProps) {
  const unavailable = isError && summary === undefined;
  const level = summary?.level ?? (unavailable ? "down" : "unknown");
  const word = unavailable
    ? "Stato dati non disponibile"
    : (summary?.word ?? (isPending ? "Stato dati…" : "Stato sconosciuto"));
  const ariaLabel = `Stato dati: ${word}${summary?.ageLabel ? ` · ${summary.ageLabel}` : ""}`;

  return (
    <Link
      to="/status"
      className={`collector-status collector-status--${level}`}
      aria-label={ariaLabel}
    >
      <span className="collector-status__dot" aria-hidden="true" />
      <span className="collector-status__text">
        {word}
        {summary?.ageLabel ? (
          <>
            {" · "}
            <span className="collector-status__age">{summary.ageLabel}</span>
          </>
        ) : null}
      </span>
    </Link>
  );
}

export function CollectorStatus() {
  const { data, isPending, isError } = useGetCollectorStatus({
    query: { refetchInterval: REFETCH_INTERVAL_MS },
  });

  return (
    <CollectorStatusView
      summary={data ? collectorSummary(data) : undefined}
      isPending={isPending}
      isError={isError}
    />
  );
}
